import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { stripApiSuffix } from '../utils/apiBaseUrl';
import { resolveNexusChatUrl } from '../utils/nexusChatUrl';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3050/api';
// Resolved by environment, never a localhost URL (see nexusChatUrl).
const NEXUSCHAT_URL = resolveNexusChatUrl();

// Marks that this tab has already tried the automatic (no-credentials) SSO
// handoff. It is the loop breaker: NexusChat bounces a session-less visitor to
// /login?redirect=nexuschat, so if we land back here after having already
// handed off once, the handoff did not stick and retrying would bounce the
// browser between www.videxa.ai and nexus.videxa.ai indefinitely. Second
// arrival always falls through to the form.
const SSO_AUTO_ATTEMPT_KEY = 'ssoAutoHandoffAttempted';

const SSO_FAILED_MESSAGE =
  'We could not automatically sign you in to NexusChat. Please sign in below to continue.';

const LoginPage = () => {
  const { login, isAuthenticated, loading: authLoading, getValidToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [requestId, setRequestId] = useState('');
  const [ssoPending, setSsoPending] = useState(false);

  // The automatic handoff runs at most once per mount, regardless of how many
  // times the effect below is re-evaluated.
  const autoHandoffStarted = useRef(false);
  // Tracks whether the component is still on screen, so an in-flight handoff
  // does not redirect a user who has already navigated away. Re-armed at the
  // top of the effect so StrictMode's mount/unmount/remount does not strand a
  // handoff that is mid-flight.
  const mounted = useRef(true);

  const wantsNexusChat = searchParams.get('redirect') === 'nexuschat';

  // Pick up ?redirect=nexuschat from URL (sent by NexusChat Login component)
  // and persist it in sessionStorage so it survives the login form submit
  useEffect(() => {
    const redirect = searchParams.get('redirect');
    if (redirect) {
      sessionStorage.setItem('redirectAfterLogin', redirect);
    }
  }, [searchParams]);

  /**
   * Exchange a valid access token for a one-time NexusChat SSO redirect URL.
   * Returns the redirect URL, or null when the backend declines to issue one.
   */
  const requestSsoRedirectUrl = useCallback(async (token) => {
    const baseUrl = stripApiSuffix(API_URL);
    const response = await fetch(`${baseUrl}/auth/sso/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ target_app: 'nexuschat' })
    });
    const data = await response.json();
    console.log('[Login] SSO generate result:', { status: response.status, success: data.success, hasRedirectUrl: !!data.redirect_url });
    return (data.success && data.redirect_url) ? data.redirect_url : null;
  }, []);

  // Already-signed-in guard: a user arriving at /login?redirect=nexuschat who
  // still holds a usable session should be handed straight through to
  // NexusChat rather than being shown a Sign In form they do not need.
  //
  // Presence of a token is NOT sufficient — an expired or malformed one would
  // produce an SSO call that 401s and bounces the user back here. getValidToken()
  // decodes the JWT, refreshes it when it is expired or about to be, and returns
  // null when the user genuinely has to re-authenticate; only a token that
  // survives that check is used. Anything else falls through to the form.
  useEffect(() => {
    mounted.current = true;
    if (autoHandoffStarted.current) return;
    if (authLoading) return;          // AuthContext is still reading storage
    if (!wantsNexusChat) return;      // only this journey auto-redirects
    if (!isAuthenticated) return;     // signed-out users get the normal form

    autoHandoffStarted.current = true;

    if (sessionStorage.getItem(SSO_AUTO_ATTEMPT_KEY)) {
      // We already handed off once in this tab and we are back — treat it as a
      // failed handoff and stop, rather than bouncing the browser again.
      setError(SSO_FAILED_MESSAGE);
      return;
    }

    setSsoPending(true);

    (async () => {
      try {
        const token = await getValidToken();
        if (!mounted.current) return;
        if (!token) {
          // Session could not be validated or refreshed — show the form.
          setSsoPending(false);
          return;
        }

        // Recorded BEFORE navigating: if NexusChat sends us back here, the
        // marker is already set and the second pass will not redirect again.
        sessionStorage.setItem(SSO_AUTO_ATTEMPT_KEY, '1');

        const redirectUrl = await requestSsoRedirectUrl(token);
        if (!mounted.current) return;

        if (redirectUrl) {
          sessionStorage.removeItem('redirectAfterLogin');
          // replace(), not assign(): keeps /login out of history so Back does
          // not re-enter the handoff.
          window.location.replace(redirectUrl);
          return; // keep the pending state up while the browser navigates
        }

        // Deliberately NOT falling back to NEXUSCHAT_URL here. Sending an
        // authenticated user to NexusChat without a token is what starts the
        // bounce loop; land on the form instead.
        setError(SSO_FAILED_MESSAGE);
      } catch (ssoErr) {
        console.error('[Login] Automatic SSO handoff failed:', ssoErr);
        if (mounted.current) setError(SSO_FAILED_MESSAGE);
      }
      if (mounted.current) setSsoPending(false);
    })();

    return () => { mounted.current = false; };
  }, [authLoading, isAuthenticated, wantsNexusChat, getValidToken, requestSsoRedirectUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setRequestId('');
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result.success) {
        // Credentials were just presented, so a subsequent arrival here is a
        // fresh journey rather than a bounce — clear the loop breaker.
        sessionStorage.removeItem(SSO_AUTO_ATTEMPT_KEY);
        const redirectPath = sessionStorage.getItem('redirectAfterLogin');
        console.log('[Login] Login success, redirectPath:', redirectPath);
        if (redirectPath) {
          sessionStorage.removeItem('redirectAfterLogin');
          if (redirectPath === 'nexuschat') {
            // User was trying to go to NexusChat — generate SSO token directly
            console.log('[Login] Redirect is nexuschat — generating SSO token...');
            try {
              const token = localStorage.getItem('auth_token');
              const redirectUrl = await requestSsoRedirectUrl(token);
              if (redirectUrl) {
                window.location.href = redirectUrl;
                return;
              }
            } catch (ssoErr) {
              console.error('[Login] SSO generate failed after login:', ssoErr);
            }
            // Fallback: go to NexusChat without SSO, or dashboard
            window.location.href = NEXUSCHAT_URL;
            return;
          } else {
            navigate(redirectPath);
          }
        } else {
          navigate('/dashboard');
        }
      } else {
        setError(result.error || 'Login failed. Please try again.');
        if (result.requestId) setRequestId(result.requestId);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Suppress the form for the moment it takes to decide whether this visitor
  // already has a session — a flash of "Sign In" immediately followed by an
  // automatic redirect reads as a bug. Only ever applies to the NexusChat
  // handoff journey; every other visit renders the form immediately.
  const checkingSession =
    wantsNexusChat && !error &&
    (authLoading || (isAuthenticated && !autoHandoffStarted.current));

  if (ssoPending || checkingSession) {
    return (
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center">
            <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-[#FFC000]">
              <div className="bg-white dark:bg-gray-800 border-b border-[#FFC000] px-8 py-6">
                <h1 className="text-3xl font-bold text-[#0E3056] dark:text-white text-center">Signing you in</h1>
              </div>
              <div className="p-8 text-center" role="status" aria-live="polite">
                <div className="h-8 w-8 mx-auto mb-4 rounded-full border-2 border-[#FFC000] border-t-transparent animate-spin" />
                <p className="text-gray-600 dark:text-gray-400">
                  Taking you to NexusChat…
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-center">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-[#FFC000]">
            <div className="bg-white dark:bg-gray-800 border-b border-[#FFC000] px-8 py-6">
              <h1 className="text-3xl font-bold text-[#0E3056] dark:text-white text-center">Sign In</h1>
            </div>

            <div className="p-8">
              {error && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                  {requestId && (
                    <p className="text-xs text-red-400 dark:text-red-500 mt-1">
                      Reference ID: <code className="font-mono">{requestId}</code>
                    </p>
                  )}
                </div>
              )}

              <form className="space-y-4" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="you@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Password
                  </label>
                  <div className="relative mt-1">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="block w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <div className="text-sm">
                    <Link to="/forgot-password" className="font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300">
                      Forgot your password?
                    </Link>
                  </div>
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-[#0E3056] bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Signing in...' : 'Sign In'}
                  </button>
                </div>
              </form>

              <div className="text-center mt-6">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Don't have an account?{' '}
                  <Link to="/register" className="font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300">
                    Create Account
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LoginPage;
